import { useState, useRef } from "react";
import { useInView } from "framer-motion";
import {
  ResponsiveContainer, LineChart, Line, XAxis, YAxis,
  CartesianGrid, Tooltip,
} from "recharts";
import {
  ShieldCheck, Sparkles, Droplets, Wind, Watch, Download,
  TrendingUp, Layers, ExternalLink, ChevronDown, BookMarked,
} from "lucide-react";
import { Reveal, SectionHeading } from "@/components/Section";
import { MARKETS, ABSOLUTE, INDEXED, PLATFORMS, MARKET_PDF, SOURCES, METHODOLOGY_RULES } from "@/lib/marketData";

const PLATFORM_ICONS = { ShieldCheck, Sparkles, Droplets, Wind, Watch };

function CustomTooltip({ active, payload, label, mode }) {
  if (!active || !payload || !payload.length) return null;
  const sorted = [...payload].sort((a, b) => b.value - a.value);
  return (
    <div className="bg-[#0b0b0b]/95 backdrop-blur border border-zinc-700 px-4 py-3 shadow-2xl">
      <p className="font-mono text-xs tracking-[0.15em] uppercase text-zinc-400 mb-2">{label}</p>
      <div className="space-y-1.5">
        {sorted.map((p) => (
          <div key={p.dataKey} className="flex items-center gap-3 text-xs">
            <span className="w-2.5 h-2.5 shrink-0" style={{ background: p.stroke }} />
            <span className="text-zinc-300 flex-1">{MARKETS.find((m) => m.key === p.dataKey)?.label}</span>
            <span className="font-mono text-white">
              {mode === "absolute" ? `$${p.value.toFixed(1)}B` : p.value.toFixed(0)}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

export function MarketConvergence() {
  const [mode, setMode] = useState("absolute");
  const [hidden, setHidden] = useState({});
  const [sourcesOpen, setSourcesOpen] = useState(false);
  const chartRef = useRef(null);
  const inView = useInView(chartRef, { once: true, amount: 0.35 });
  const data = mode === "absolute" ? ABSOLUTE : INDEXED;

  const toggle = (key) => setHidden((h) => ({ ...h, [key]: !h[key] }));

  return (
    <section className="border-t border-zinc-800 bg-[#0d0d0d] overflow-hidden" data-testid="market-convergence">
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-24 md:py-32">
        <SectionHeading
          overline="Market Convergence · 2022–2035"
          title="One platform, positioned across converging markets."
          subtitle="Commercial proof for the thesis. WearSpray is adjacent to the topical-formulation, wearable-hardware and behavioral-access markets it can be licensed, integrated or adapted into. These are adjacent-market environments, not WearSpray revenue projections."
          testId="market-convergence-heading"
        />

        {/* INTERACTIVE CHART */}
        <Reveal delay={0.1}>
          <div className="mt-16 md:mt-20 border border-zinc-800 bg-[#0b0b0b]">
            {/* mode toggle */}
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 p-5 md:p-6 border-b border-zinc-800">
              <div>
                <p className="font-heading text-lg md:text-xl font-light tracking-tight">
                  {mode === "absolute" ? "Global revenue scale" : "Indexed growth (2024 = 100)"}
                </p>
                <p className="font-body text-xs text-zinc-500 mt-1">
                  {mode === "absolute"
                    ? "Logarithmic scale · USD billions · so smaller categories stay visible."
                    : "Every market set to 100 in 2024 to reveal relative momentum."}
                </p>
              </div>
              <div className="inline-flex border border-zinc-800 self-start" data-testid="chart-mode-toggle">
                {[
                  { id: "absolute", label: "Absolute Scale" },
                  { id: "indexed", label: "Growth Index" },
                ].map((t) => (
                  <button
                    key={t.id}
                    onClick={() => setMode(t.id)}
                    data-testid={`chart-mode-${t.id}`}
                    aria-pressed={mode === t.id}
                    className={`font-mono text-[11px] tracking-[0.12em] uppercase px-4 py-2.5 transition-colors duration-300 ${
                      mode === t.id ? "bg-[#00e5ff] text-black" : "bg-transparent text-zinc-400 hover:text-white"
                    }`}
                  >
                    {t.label}
                  </button>
                ))}
              </div>
            </div>

            {/* chart */}
            <div className="p-4 md:p-6">
              <div ref={chartRef} className="h-[380px] md:h-[460px] w-full" data-testid="market-chart">
                {inView && (
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={data} margin={{ top: 12, right: 16, left: 4, bottom: 4 }}>
                    <CartesianGrid stroke="#1f1f1f" vertical={false} />
                    <XAxis
                      dataKey="year"
                      stroke="#52525b"
                      tick={{ fill: "#71717a", fontSize: 12, fontFamily: "monospace" }}
                      tickLine={false}
                      axisLine={{ stroke: "#27272a" }}
                    />
                    <YAxis
                      scale={mode === "absolute" ? "log" : "linear"}
                      domain={mode === "absolute" ? [0.3, 600] : [70, "auto"]}
                      allowDataOverflow
                      stroke="#52525b"
                      tick={{ fill: "#71717a", fontSize: 11, fontFamily: "monospace" }}
                      tickLine={false}
                      axisLine={{ stroke: "#27272a" }}
                      width={44}
                      tickFormatter={(v) => (mode === "absolute" ? `$${v}` : v)}
                    />
                    <Tooltip content={<CustomTooltip mode={mode} />} cursor={{ stroke: "#3f3f46", strokeDasharray: "4 4" }} />
                    {MARKETS.map((m, i) =>
                      hidden[m.key] ? null : (
                        <Line
                          key={m.key}
                          type="monotone"
                          dataKey={m.key}
                          stroke={m.color}
                          strokeWidth={m.key === "skincare" || m.key === "smartwatch" ? 2.5 : 1.75}
                          dot={false}
                          activeDot={{ r: 4, strokeWidth: 0 }}
                          isAnimationActive
                          animationBegin={i * 140}
                          animationDuration={1300}
                          animationEasing="ease-in-out"
                        />
                      )
                    )}
                  </LineChart>
                </ResponsiveContainer>
                )}
              </div>

              {/* interactive legend */}
              <div className="flex flex-wrap gap-2.5 mt-6" data-testid="chart-legend">
                {MARKETS.map((m) => {
                  const off = hidden[m.key];
                  return (
                    <button
                      key={m.key}
                      onClick={() => toggle(m.key)}
                      data-testid={`legend-${m.key}`}
                      className={`inline-flex items-center gap-2 border px-3 py-1.5 transition-all duration-300 ${
                        off ? "border-zinc-800 opacity-40 hover:opacity-70" : "border-zinc-700 hover:border-zinc-500"
                      }`}
                    >
                      <span className="w-3 h-3 shrink-0" style={{ background: m.color, opacity: off ? 0.4 : 1 }} />
                      <span className="font-body text-xs text-zinc-300">{m.label}</span>
                      <span className="font-mono text-[10px] text-zinc-500">{m.cagr}%</span>
                    </button>
                  );
                })}
              </div>
              <p className="font-body text-[11px] text-zinc-600 leading-relaxed mt-5">
                Tap a market to toggle it. CAGR shown per market. Values 2031–2035 are directional
                extrapolations using published CAGRs (Grand View Research), not third-party forecasts.
              </p>
            </div>
          </div>
        </Reveal>

        {/* 2035 RANKED CARDS */}
        <Reveal delay={0.1}>
          <div className="mt-16" data-testid="market-2035-grid">
            <p className="font-mono text-xs tracking-[0.25em] uppercase text-zinc-500 mb-8">
              Modeled 2035 Global Revenue · Adjacent Markets
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
              {MARKETS.map((m) => (
                <div key={m.key} className="border border-zinc-800 bg-[#111] p-5 hover:border-zinc-600 transition-colors duration-500">
                  <div className="flex items-center gap-2">
                    <span className="w-2.5 h-2.5 shrink-0" style={{ background: m.color }} />
                    <span className="font-body text-xs text-zinc-400 leading-snug">{m.label}</span>
                  </div>
                  <p className="font-heading text-2xl md:text-3xl font-light tracking-tight mt-4">
                    ${m.rev2035 >= 100 ? m.rev2035.toFixed(0) : m.rev2035.toFixed(1)}B
                  </p>
                  <div className="flex items-center gap-1.5 mt-2" style={{ color: "#00e5ff" }}>
                    <TrendingUp size={13} strokeWidth={1.75} />
                    <span className="font-mono text-[11px]">{m.cagr}% CAGR</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </Reveal>

        {/* PLATFORM OPPORTUNITIES */}
        <Reveal delay={0.1}>
          <div className="mt-20 md:mt-28" data-testid="platform-opportunities">
            <h3 className="font-heading text-2xl md:text-4xl font-light tracking-tight leading-snug max-w-3xl">
              One protected dispensing platform.{" "}
              <span style={{ color: "#e6c068" }}>Multiple formulation industries.</span>
            </h3>
            <p className="font-body text-zinc-400 leading-relaxed mt-5 max-w-2xl">
              The strongest thesis is not one band with one formulation. It is a category
              architecture: the hardware layer that moves a product from a bottle in a bag to
              an accessible position on the body.
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-px mt-12 bg-zinc-800 border border-zinc-800">
              {PLATFORMS.map((p, i) => {
                const Icon = PLATFORM_ICONS[p.icon] || Layers;
                return (
                  <Reveal key={p.title} delay={i * 0.06}>
                    <div className="bg-[#121212] p-6 h-full hover:bg-[#161616] transition-colors duration-500">
                      <Icon size={20} strokeWidth={1.5} style={{ color: "#00e5ff" }} />
                      <h4 className="font-heading text-base font-medium mt-4">{p.title}</h4>
                      <p className="font-body text-xs text-zinc-500 leading-relaxed mt-2">{p.body}</p>
                    </div>
                  </Reveal>
                );
              })}
            </div>
          </div>
        </Reveal>

        {/* KEY INSIGHT + DOWNLOAD */}
        <Reveal delay={0.1}>
          <div className="mt-20 md:mt-28 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-center border-t border-zinc-800 pt-16">
            <div className="lg:col-span-7">
              <p className="font-heading text-xl md:text-2xl font-light tracking-tight leading-snug pl-6 border-l-2" style={{ borderColor: "#00e5ff" }}>
                A growing wrist ecosystem, plus expanding topical formulation markets, plus rising
                demand for immediate, repeated and personalized application.{" "}
                <span style={{ color: "#e6c068" }}>
                  That is where the convergence becomes more valuable than any single category.
                </span>
              </p>
            </div>
            <div className="lg:col-span-5">
              <a
                href={MARKET_PDF}
                target="_blank"
                rel="noopener noreferrer"
                download
                data-testid="market-workbook-download"
                className="group inline-flex items-center gap-3 font-body text-sm font-medium bg-[#00e5ff] text-black px-8 py-4 transition-transform hover:-translate-y-0.5"
              >
                <Download size={16} />
                Download the Market Convergence Workbook
              </a>
              <p className="font-mono text-[11px] tracking-[0.15em] uppercase text-zinc-600 mt-4">
                XLSX · Full 2022–2035 projections, sources & methodology
              </p>
            </div>
          </div>
        </Reveal>

        {/* SOURCES & METHODOLOGY */}
        <Reveal delay={0.1}>
          <div className="mt-16 border border-zinc-800 bg-[#0b0b0b]" data-testid="market-sources">
            <button
              onClick={() => setSourcesOpen((o) => !o)}
              data-testid="market-sources-toggle"
              aria-expanded={sourcesOpen}
              className="w-full flex items-center justify-between gap-4 px-5 md:px-6 py-5 text-left group"
            >
              <span className="flex items-center gap-3">
                <BookMarked size={16} style={{ color: "#00e5ff" }} />
                <span className="font-heading text-base md:text-lg font-light tracking-tight">
                  Sources &amp; Methodology
                </span>
                <span className="font-mono text-[10px] tracking-[0.15em] uppercase text-zinc-500 hidden sm:inline">
                  Grand View Research · verified
                </span>
              </span>
              <ChevronDown
                size={18}
                className="text-zinc-500 transition-transform duration-300 group-hover:text-zinc-300"
                style={{ transform: sourcesOpen ? "rotate(180deg)" : "none" }}
              />
            </button>

            {sourcesOpen && (
              <div className="px-5 md:px-6 pb-8 border-t border-zinc-800 pt-6">
                {/* per-market source table */}
                <div className="overflow-x-auto">
                  <table className="w-full text-left border-collapse min-w-[640px]">
                    <thead>
                      <tr className="border-b border-zinc-800">
                        {["Market", "Published Base", "CAGR", "Confidence", "Source"].map((h) => (
                          <th key={h} className="font-mono text-[10px] tracking-[0.15em] uppercase text-zinc-500 font-normal py-3 pr-4">{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {SOURCES.map((s) => {
                        const m = MARKETS.find((mk) => mk.key === s.key);
                        return (
                          <tr key={s.key} className="border-b border-zinc-900 align-top">
                            <td className="py-4 pr-4">
                              <span className="flex items-center gap-2">
                                <span className="w-2.5 h-2.5 shrink-0" style={{ background: m?.color }} />
                                <span className="font-body text-sm text-zinc-200">{s.label}</span>
                              </span>
                              <span className="font-body text-xs text-zinc-600 leading-snug block mt-1.5 max-w-xs">{s.boundary}</span>
                            </td>
                            <td className="py-4 pr-4 font-mono text-xs text-zinc-300 whitespace-nowrap">${s.base}B <span className="text-zinc-600">({s.baseYear})</span></td>
                            <td className="py-4 pr-4 font-mono text-xs whitespace-nowrap" style={{ color: "#00e5ff" }}>{s.cagr}%</td>
                            <td className="py-4 pr-4 font-mono text-[11px] text-zinc-400 whitespace-nowrap">{s.confidence}</td>
                            <td className="py-4 pr-4">
                              <a
                                href={s.url}
                                target="_blank"
                                rel="noopener noreferrer"
                                data-testid={`source-link-${s.key}`}
                                className="inline-flex items-center gap-1.5 font-body text-xs text-zinc-400 hover:text-[#00e5ff] transition-colors"
                              >
                                Grand View Research <ExternalLink size={12} />
                              </a>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>

                {/* methodology rules */}
                <div className="mt-8">
                  <p className="font-mono text-[10px] tracking-[0.2em] uppercase text-zinc-500 mb-4">How the model works</p>
                  <ul className="grid grid-cols-1 md:grid-cols-2 gap-x-10 gap-y-3">
                    {METHODOLOGY_RULES.map((r, i) => (
                      <li key={r} className="flex items-start gap-3">
                        <span className="font-mono text-[10px] mt-0.5" style={{ color: "#00e5ff" }}>{String(i + 1).padStart(2, "0")}</span>
                        <span className="font-body text-xs text-zinc-400 leading-relaxed">{r}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                <p className="font-body text-[11px] text-zinc-600 leading-relaxed mt-8 border-t border-zinc-900 pt-5">
                  Each value is calculated from the source's published base using its published CAGR. Figures for
                  2031–2035 are directional extrapolations of those same CAGRs, not third-party forecasts. Base
                  figures were independently verified against the linked Grand View Research reports.
                </p>
              </div>
            )}
          </div>
        </Reveal>
      </div>
    </section>
  );
}
